import { LEVEL_LABELS, pct } from './format.ts';
import type { Distribution } from './types.ts';

// Horizontal stacked bar of the 1..5 maturity levels. A 2px white gap separates
// segments; identity is carried by the legend + counts (never colour alone), so
// the bar itself is role="img" with a spoken-out aria-label.
export function DistributionBar({
  distribution,
  colors,
  compact = false,
  emptyMessage = 'Distribution not available (imported at dimension level).',
}: {
  distribution: Distribution | null;
  colors: readonly string[];
  compact?: boolean;
  emptyMessage?: string;
}) {
  if (!distribution) {
    return <p className="text-sm italic text-ink-soft">{emptyMessage}</p>;
  }

  const levels = ([0, 1, 2, 3, 4] as const).map((i) => ({
    level: i + 1,
    count: distribution.counts[i] ?? 0,
    percent: distribution.pct[i] ?? 0,
    color: colors[i] ?? 'var(--accent)',
    label: LEVEL_LABELS[i],
  }));

  const ariaLabel =
    `Distribution over ${distribution.total} responses — ` +
    levels.map((l) => `${l.label}: ${pct(l.percent)}`).join(', ');

  return (
    <div>
      <div
        className={`flex w-full overflow-hidden rounded-lg bg-black/5 ${compact ? 'h-3' : 'h-9'}`}
        role="img"
        aria-label={ariaLabel}
      >
        {levels
          .filter((l) => l.percent > 0)
          .map((l) => (
            <div
              key={l.level}
              className="flex items-center justify-center border-r-2 border-white last:border-r-0"
              style={{ width: `${l.percent}%`, background: l.color }}
              title={`${l.label}: ${l.count} (${pct(l.percent)})`}
            >
              {!compact && l.percent >= 9 && (
                <span
                  className="text-[11px] font-semibold tabular-nums"
                  style={{ color: l.level >= 4 ? '#ffffff' : '#1a1a1a' }}
                >
                  {pct(l.percent)}
                </span>
              )}
            </div>
          ))}
      </div>

      {!compact && (
        <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-ink-soft">
          {levels.map((l) => (
            <li key={l.level} className="flex items-center gap-1.5">
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm ring-1 ring-black/10"
                style={{ background: l.color }}
                aria-hidden
              />
              <span>{l.label}</span>
              <span className="font-medium text-ink tabular-nums">
                {l.count} ({pct(l.percent)})
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
